import { Container, Graphics, Rectangle } from 'pixi.js';
import { Scene } from '../app/SceneManager';
import { COLORS, DESIGN_W, TYPE } from '../app/theme';
import { Button, makeText, sceneBackground, glassCard, avatarSprite, iconSprite } from '../ui/widgets';
import { TextField, type TextFieldOpts } from '../ui/TextField';
import { ModalCard } from '../ui/ModalCard';
import { AvatarPickerModal } from '../ui/AvatarPickerModal';
import { pageHeader, enterFade, enterPop } from './common';
import type { AppContext } from '../app/AppContext';
import { MessageTag } from '../net/messageTags';
import { encodeGetProfile, encodeSaveProfile, encodeChangePassword, encodeSaveAvatar, decodeProfile } from '../net/codec/profile';
import { buildSaveProfile, type ProfileModel } from '../net/models/profileModel';
import { gameState } from '../state/GameState';
import { encodeLogout } from '../net/codec/lobby';
import { RoomName } from '../net/enums';
import { clearLoginCredentials, saveLoginCredentials } from '../platform/storage';

/**
 * Profile + avatar + password (port of ProfilePageCode.cs / AvatarSelector.cs), re-skinned to the
 * Login/Register glass-card style: an identity header card (tappable avatar + read-only username /
 * email / mobile) and a bank-info card of iconified fields, with «تغییر آواتار» opening the
 * AvatarPickerModal and «تغییر رمز» opening a restyled ModalCard password form. All codec wiring
 * (GetProfile/SaveProfile/SaveAvatar/ChangePassword) is unchanged; layout is height-responsive.
 */
export class ProfileScene extends Scene {
  private profile: ProfileModel | null = null;
  private readonly body = new Container();
  private entered = false;

  private avatarIndex = 0;
  private avatarModal?: AvatarPickerModal;
  private passwordOverlay!: ModalCard;
  private headerChrome?: Container;

  private fields: Record<string, TextField> = {};
  private oldPass?: TextField;
  private newPass?: TextField;
  private confirmPass?: TextField;
  private pendingNewPassword: string | null = null;

  constructor(private readonly ctx: AppContext) {
    super();
  }

  onEnter(): void {
    this.addChild(sceneBackground());
    this.headerChrome = pageHeader('پروفایل', () => this.ctx.navigate(RoomName.MainMenu), this.ctx.iconTexture('user'));
    this.addChild(this.headerChrome);
    this.addChild(this.body);
    this.avatarIndex = gameState.get().avatar;

    this.track(
      this.ctx.client.on(MessageTag.GetProfile, (r) => {
        this.profile = decodeProfile(r);
        this.build();
      }),
    );
    this.track(
      this.ctx.client.on(MessageTag.SendMessage, (r) => {
        if (!this.pendingNewPassword) return;
        const message = r.getString();
        if (message === 'رمز تغییر کرد.') {
          void saveLoginCredentials(gameState.get().username, this.pendingNewPassword);
          this.pendingNewPassword = null;
        } else if (message === 'قادر به تغییر رمز نمی باشد.' || message === 'رمز نادرست است.') {
          this.pendingNewPassword = null;
        }
      }),
    );
    this.ctx.client.send(encodeGetProfile());
  }

  /** Re-flow on viewport-height change (Telegram fullscreen resolves after boot). Preserve typed edits. */
  onResize(): void {
    this.rebuild();
  }

  /** Rebuild the layout while carrying over any values the user has typed into the bank fields. */
  private rebuild(): void {
    if (!this.profile) return;
    const vals: Record<string, string> = {};
    for (const k in this.fields) vals[k] = this.fields[k].value;
    this.build();
    for (const k in vals) if (this.fields[k]) this.fields[k].value = vals[k];
  }

  private build(): void {
    const p = this.profile;
    if (!p) return;
    for (const child of this.body.removeChildren()) child.destroy({ children: true });
    this.fields = {};
    const w = DESIGN_W - 80;
    const left = 40;
    const pad = 24;
    const innerW = w - pad * 2;

    // ── Identity header card: tappable avatar (right/leading) + read-only username/email/mobile. ──
    const idY = 104;
    const idH = 190;
    this.body.addChild(glassCard(left, idY, w, idH));

    const avSize = 104;
    const avX = left + w - pad - avSize;
    const avY = idY + 22;
    const holder = new Container();
    holder.addChild(avatarSprite(this.ctx.profileAtlas, this.avatarIndex, avSize));
    // Camera badge bottom-right marks the avatar as editable (dark chip + gold camera icon).
    const badge = new Container();
    badge.addChild(
      new Graphics().circle(0, 0, 18).fill({ color: COLORS.panel }).stroke({ width: 2, color: COLORS.accent }),
    );
    const cam = iconSprite(this.ctx.iconTexture('camera'), 24);
    cam.position.set(-12, -12);
    badge.addChild(cam);
    badge.position.set(avSize - 8, avSize - 8);
    holder.addChild(badge);
    holder.position.set(avX, avY);
    holder.eventMode = 'static';
    holder.cursor = 'pointer';
    holder.hitArea = new Rectangle(0, 0, avSize, avSize);
    holder.on('pointertap', () => this.openAvatar());
    holder.on('pointerover', () => (holder.alpha = 0.85));
    holder.on('pointerout', () => (holder.alpha = 1));
    this.body.addChild(holder);

    const avHint = makeText('تغییر آواتار', { size: 22, fill: COLORS.gold, align: 'center' });
    avHint.anchor.set(0.5, 0);
    avHint.position.set(avX + avSize / 2, avY + avSize + 6);
    this.body.addChild(avHint);

    const rightX = avX - 24;
    const uname = makeText(p.username || '—', { size: TYPE.heading, weight: '700', fill: COLORS.text });
    uname.anchor.set(1, 0);
    uname.position.set(rightX, idY + 34);
    this.body.addChild(uname);
    this.identityLine(p.email, rightX, idY + 80, this.ctx.iconTexture('email'));
    this.identityLine(p.mobile, rightX, idY + 118, this.ctx.iconTexture('phone'));

    const logout = new Button('خروج', () => this.logout(), {
      width: 150,
      height: 64,
      fontSize: 24,
      fill: COLORS.danger,
      accessibleLabel: 'خروج از حساب کاربری',
    });
    logout.position.set(left + 20, idY + idH - 82);
    this.body.addChild(logout);

    // ── One-time-entry warning, just above the bank card. ──
    const note = makeText('اطلاعات بانکی پس از ثبت، فقط با پشتیبانی قابل تغییر است.', { size: 23, fill: COLORS.gold, align: 'center' });
    note.anchor.set(0.5, 0);
    note.position.set(DESIGN_W / 2, idY + idH + 14);
    this.body.addChild(note);

    // ── Bank-info card: heading + 5 iconified fields, with the shaba helpers right under shaba. ──
    const bankY = idY + idH + 50;
    const fieldH = 76;
    const rowPitch = 108;
    const helperH = 76;
    const bankH = 18 + rowPitch * 5 + helperH + 14;
    this.body.addChild(glassCard(left, bankY, w, bankH));

    const fieldX = left + pad;
    const editableBankFields: TextField[] = [];
    let cy = bankY + 18;
    const addField = (
      key: string,
      caption: string,
      value: string,
      readonly: boolean,
      icon?: ReturnType<AppContext['iconTexture']>,
      opts: Partial<TextFieldOpts> = {},
    ): void => {
      const label = makeText(caption, { size: TYPE.label, fill: COLORS.textDim, align: 'right' });
      label.anchor.set(1, 0);
      label.position.set(left + w - pad, cy);
      this.body.addChild(label);
      const f = new TextField({
        width: innerW,
        height: fieldH,
        value,
        align: 'right',
        icon: icon ?? null,
        accessibilityLabel: caption,
        enterKeyHint: 'next',
        ...opts,
      });
      f.position.set(fieldX, cy + 30);
      f.setReadonly(readonly);
      this.body.addChild(f);
      this.fields[key] = f;
      if (!readonly) editableBankFields.push(f);
      cy += rowPitch;
    };

    addField('cardHolderName', 'نام صاحب کارت', p.cardHolderName, p.cardHolderNameReadonly, this.ctx.iconTexture('user'));
    addField('shaba', 'شماره شبا', p.shaba, p.shabaReadonly, this.ctx.iconTexture('bank'));

    // Shaba helpers — placed directly under the shaba field so the relationship is obvious.
    const helperHalf = (innerW - 16) / 2;
    const getShaba = new Button('گرفتن شبا', () => this.ctx.dialog({ message: 'استعلام شبا در نسخه وب فعال نیست.' }), {
      width: helperHalf,
      height: 64,
      fontSize: 24,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
      icon: this.ctx.iconTexture('search'),
    });
    getShaba.position.set(fieldX + helperHalf + 16, cy);
    this.body.addChild(getShaba);
    const paste = new Button('چسباندن', () => void this.pasteShaba(), {
      width: helperHalf,
      height: 64,
      fontSize: 24,
      fill: COLORS.panelSoft,
      textColor: COLORS.text,
    });
    paste.position.set(fieldX, cy);
    this.body.addChild(paste);
    cy += helperH;

    addField('cardNumber', 'شماره کارت', p.cardNumber, p.cardNumberReadonly, this.ctx.iconTexture('credit-card'), { inputMode: 'numeric' });
    addField('accountNumber', 'شماره حساب', p.accountNumber, p.accountNumberReadonly, this.ctx.iconTexture('bank'), { inputMode: 'numeric' });
    addField('bankName', 'نام بانک', p.bankName, p.bankNameReadonly, this.ctx.iconTexture('bank'));

    editableBankFields.forEach((field, index) => {
      const next = editableBankFields[index + 1];
      field.setEnterKeyHint(next ? 'next' : 'done');
      field.onKeyboardEnter(next ? () => next.focus() : () => this.saveProfile());
    });

    // Actions follow the form naturally; Telegram supplies the native back affordance.
    const actionsY = bankY + bankH + 16;
    const passBtn = new Button('تغییر رمز', () => {
      this.passwordOverlay.visible = true;
      this.oldPass?.focus();
    }, {
      width: w,
      height: 76,
      fontSize: 26,
      fill: COLORS.panel,
      textColor: COLORS.text,
      icon: this.ctx.iconTexture('lock'),
    });
    passBtn.position.set(left, actionsY);
    this.body.addChild(passBtn);

    const save = new Button('ذخیره اطلاعات بانکی', () => this.saveProfile(), {
      width: w,
      height: 88,
      fontSize: 28,
      icon: this.ctx.iconTexture('save'),
    });
    save.position.set(left, actionsY + 92);
    this.body.addChild(save);

    this.passwordOverlay?.destroy({ children: true });
    this.buildPasswordModal();
    // Keep the shared header above the rebuilt body, while modals added afterward still cover it.
    if (this.headerChrome) this.addChild(this.headerChrome);

    if (!this.entered) {
      this.entered = true;
      enterFade([this.body]);
      enterPop([holder]);
    }
  }

  /** A right-aligned read-only identity line with a small leading (right-edge) icon. */
  private identityLine(text: string, rightX: number, y: number, icon?: ReturnType<AppContext['iconTexture']>): void {
    const t = makeText(text || '—', { size: 25, fill: COLORS.textDim, align: 'right' });
    t.anchor.set(1, 0);
    const iconSize = 22;
    t.position.set(rightX - (icon ? iconSize + 8 : 0), y);
    this.body.addChild(t);
    if (icon) {
      const node = iconSprite(icon, iconSize);
      node.alpha = 0.85;
      node.position.set(rightX - iconSize, y - 1);
      this.body.addChild(node);
    }
  }

  private openAvatar(): void {
    if (this.avatarModal) return;
    this.avatarIndex = gameState.get().avatar;
    const modal = new AvatarPickerModal(
      this.ctx.profileAtlas,
      this.avatarIndex,
      (idx) => {
        this.avatarIndex = idx;
        this.saveAvatar();
        this.closeAvatar();
        this.rebuild(); // reflect the new avatar in the identity card, keeping typed bank edits
      },
      () => this.closeAvatar(),
    );
    this.avatarModal = modal;
    this.addChild(modal);
  }

  private closeAvatar(): void {
    this.avatarModal?.destroy({ children: true });
    this.avatarModal = undefined;
  }

  private saveAvatar(): void {
    this.ctx.client.send(encodeSaveAvatar(this.avatarIndex));
    gameState.update({ avatar: this.avatarIndex });
  }

  // --- Password sub-modal: old / new / confirm, on a restyled card with field icons --------------

  private buildPasswordModal(): void {
    const modal = new ModalCard({
      title: 'تغییر رمز',
      width: DESIGN_W - 80,
      height: 600,
      onDismiss: () => (modal.visible = false),
    });
    modal.visible = false;
    const fx = 40;
    const fw = modal.cardWidth - 80;
    let y = modal.contentTop + 8;

    // Lock emblem under the title.
    const emblem = iconSprite(this.ctx.iconTexture('lock'), 52);
    emblem.position.set(modal.cardWidth / 2 - 26, y);
    modal.content.addChild(emblem);
    y += 64;

    this.oldPass = new TextField({
      width: fw,
      secure: true,
      placeholder: 'رمز عبور قدیم',
      align: 'right',
      icon: this.ctx.iconTexture('lock'),
      autocomplete: 'current-password',
      enterKeyHint: 'next',
    });
    this.oldPass.position.set(fx, y);
    modal.content.addChild(this.oldPass);
    y += 84;
    this.newPass = new TextField({
      width: fw,
      secure: true,
      placeholder: 'رمز عبور جدید',
      align: 'right',
      icon: this.ctx.iconTexture('key'),
      autocomplete: 'new-password',
      enterKeyHint: 'next',
    });
    this.newPass.position.set(fx, y);
    modal.content.addChild(this.newPass);
    y += 84;
    this.confirmPass = new TextField({
      width: fw,
      secure: true,
      placeholder: 'رمز عبور جدید مجدد',
      align: 'right',
      icon: this.ctx.iconTexture('key'),
      autocomplete: 'new-password',
      enterKeyHint: 'done',
    });
    this.confirmPass.position.set(fx, y);
    modal.content.addChild(this.confirmPass);
    this.oldPass.onKeyboardEnter(() => this.newPass?.focus());
    this.newPass.onKeyboardEnter(() => this.confirmPass?.focus());
    this.confirmPass.onKeyboardEnter(() => this.changePassword());
    y += 100;

    const change = new Button('تغییر رمز', () => this.changePassword(), { width: fw, height: 64, fontSize: 24, icon: this.ctx.iconTexture('save') });
    change.position.set(fx, y);
    modal.content.addChild(change);
    y += 78;
    const back = new Button('بازگشت', () => (modal.visible = false), {
      width: fw,
      height: 56,
      fontSize: 22,
      fill: COLORS.panel,
      textColor: COLORS.text,
    });
    back.position.set(fx, y);
    modal.content.addChild(back);

    this.passwordOverlay = modal;
    this.addChild(modal);
  }

  private async pasteShaba(): Promise<void> {
    try {
      const text = await navigator.clipboard.readText();
      if (text && this.fields.shaba) this.fields.shaba.value = text.trim();
    } catch {
      this.ctx.dialog({ message: 'دسترسی به کلیپ‌بورد ممکن نیست.' });
    }
  }

  private saveProfile(): void {
    const p = this.profile;
    if (!p) return;
    const model = buildSaveProfile({
      username: p.username,
      email: p.email,
      mobile: p.mobile,
      cardHolderName: this.fields.cardHolderName.value,
      shaba: this.fields.shaba.value,
      cardNumber: this.fields.cardNumber.value,
      accountNumber: this.fields.accountNumber.value,
      bankName: this.fields.bankName.value,
    });
    this.ctx.client.send(encodeSaveProfile(model));
  }

  private changePassword(): void {
    if (!this.oldPass || !this.newPass || !this.confirmPass) return;
    if (this.newPass.value !== this.confirmPass.value) {
      this.ctx.dialog({ message: 'رمز جدید و تکرار آن یکسان نیستند.' });
      return;
    }
    this.pendingNewPassword = this.newPass.value;
    this.ctx.client.send(encodeChangePassword(this.oldPass.value, this.pendingNewPassword));
    this.oldPass.value = '';
    this.newPass.value = '';
    this.confirmPass.value = '';
    this.passwordOverlay.visible = false;
  }

  private async logout(): Promise<void> {
    await clearLoginCredentials();
    this.ctx.client.send(encodeLogout());
  }
}
